import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { industries } from "@/data/industries";
import { caseStudies } from "@/data/caseStudies";
import SEOHead from "@/components/SEOHead";

export default function IndustryDetailPage() {
  const { slug } = useParams();
  const industry = industries.find((i) => i.slug === slug);

  if (!industry) return <Navigate to="/industries" replace />;

  const Icon = industry.icon;
  const related = caseStudies.filter((cs) =>
    industry.relatedCaseStudies?.includes(cs.slug)
  );

  return (
    <main data-testid="industry-detail-page">
      <SEOHead
        title={`${industry.title} — Venury Industries`}
        description={industry.shortDesc}
        url={`https://venury.tech/industries/${slug}`}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-10%", right: "-5%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link
              to="/industries"
              className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200"
            >
              <ArrowLeft size={14} /> All Industries
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="w-16 h-16 rounded-xl bg-teal-primary/10 flex items-center justify-center">
              <Icon size={32} className="text-teal-bright" />
            </div>
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
              Industry Focus
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight max-w-4xl"
          >
            {industry.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg text-slate-400 leading-relaxed max-w-2xl mt-6"
          >
            {industry.heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap gap-6 mt-10"
          >
            {industry.stats.map((stat, i) => (
              <div key={i} className="px-5 py-3 rounded-lg bg-white/5 border border-white/5">
                <span className="font-mono text-sm text-teal-bright">{stat}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">The Challenge</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              Industry Pain Points We Solve
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.1}>
            {industry.challenges.map((challenge, i) => (
              <StaggerItem key={i}>
                <div className="p-6 rounded-lg border border-white/5 hover:border-orange-primary/20 transition-colors duration-300 h-full">
                  <div className="w-10 h-10 rounded-lg bg-orange-primary/10 flex items-center justify-center mb-5">
                    <span className="font-mono text-sm font-bold text-orange-bright">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{challenge}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 md:py-28 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Our Approach</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              How We Address Them
            </h2>
          </ScrollReveal>

          <div className="space-y-6">
            {industry.solutions.map((solution, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="flex items-start gap-4 p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300">
                  <div className="w-8 h-8 rounded-full bg-teal-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={16} className="text-teal-bright" />
                  </div>
                  <p className="text-base text-slate-400 leading-relaxed">{solution}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Capabilities</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              What We Build
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
            {industry.capabilities.map((cap, i) => (
              <StaggerItem key={i}>
                <div className="glass-card rounded-lg p-6 h-full group hover:border-teal-primary/30 transition-colors duration-300">
                  <h3 className="font-heading text-base font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200">
                    {cap.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{cap.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-6">Technology Stack</span>
            <div className="flex flex-wrap gap-3">
              {industry.techStack.map((tech) => (
                <span key={tech} className="font-mono text-sm text-slate-400 px-4 py-2 rounded border border-white/5 bg-white/[0.02]">
                  {tech}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related Case Studies */}
      {related.length > 0 && (
        <section className="py-20 md:py-28 bg-[#080C10]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <ScrollReveal>
              <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Proven Results</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
                Related Case Studies
              </h2>
            </ScrollReveal>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
              {related.map((cs) => (
                <StaggerItem key={cs.slug}>
                  <Link
                    to={`/work/${cs.slug}`}
                    className="block glass-card rounded-lg overflow-hidden group h-full flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={cs.image}
                        alt={cs.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded backdrop-blur-sm">
                        {cs.category}
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200">
                        {cs.title}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed flex-1">{cs.shortDesc}</p>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}
    </main>
  );
}
